import { createApiClient } from './httpSetup';

/**
 * @description
 * @param {string} tabKey
 * @returns {Promise<Object>} 
 */

const apiClient = createApiClient({
  baseURL: '/api',
});

const handleApiError = (error, contextMessage = 'API请求失败') => {
  console.error(`${contextMessage}:`, error.response || error.message || error);
  const serverError = error.response?.data?.error || error.response?.data?.detail || error.response?.data?.message;
  const fallbackMessage = '操作失败，请检查网络或联系管理员。';
  throw new Error(serverError || fallbackMessage);
};

/**
 * @param {string} currentTab
 * @param {object} params
 * @returns {Promise<object>}
 */
export const loadAdminTableData = async (currentTab, params = {}) => {
  const url = `/admintable/tabledata/${currentTab}`;
  let queryParams = { ...params };
  if (queryParams.filters && typeof queryParams.filters === 'object') {
    queryParams.filters = JSON.stringify(queryParams.filters);
  }

  console.log(`[adminApiService] Calling loadAdminTableData for tab: ${currentTab}, URL: ${url}, Params:`, queryParams);

  try {
    const response = await apiClient.get(url, { params: queryParams });

    // 修正后的返回对象
    return {
      data: response.data?.data || [],
      total: response.data?.total || 0,
      headers: response.data?.headers || [], // <--- 在这里加上这一行
      tableLastUpdate: response.data?.tableLastUpdate || null
    };
  } catch (error) {
    // throw 关键字会确保错误能被外层捕获并显示 alert
    throw handleApiError(error, `加载 Admin Tab [${currentTab}] 数据失败`);
  }
};

/** 解析 Excel 表头并返回建议列映射 */
export const parseExcelUpload = async (tableName, formData) => {
  try {
    const response = await apiClient.post(`/upload/parse-upload?tableName=${tableName}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    return handleApiError(error, '解析 Excel 失败');
  }
};

/** 冲突预检测（不落库，返回 diff 列表） */
export const checkUploadConflicts = async (tableName, formData) => {
  try {
    const response = await apiClient.post(`/upload/check-conflicts?tableName=${tableName}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    return handleApiError(error, '冲突检测失败');
  }
};

/** 带列映射上传（直接入库） */
export const uploadExcelMappedData = async (tableName, formData) => {
  try {
    const response = await apiClient.post(`/upload/append-mapped?tableName=${tableName}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    return handleApiError(error, `上传数据到表 ${tableName} 失败`);
  }
};

/** 带列映射解析（编辑模式临时预览） */
export const uploadExcelMappedToTemp = async (formData, tableName) => {
  try {
    const response = await apiClient.post(`/upload/parse-mapped?tableName=${tableName}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    return handleApiError(error, '解析 Excel 文件失败');
  }
};

/**
 * @param {string} tableName
 * @param {string|number} rowId
 * @param {string|null} newStatus
 * @returns {Promise<object>}
 */
export const updateRowStatusInDb = async (tableName, rowId, newStatus) => {
  try {
    const response = await apiClient.put(`/adminpage/status/${tableName}/${rowId}`, { status: newStatus });
    return response.data;
  } catch (error) {
    return handleApiError(error, `更新行 ${rowId} (表: ${tableName}) 状态为 ${newStatus} 失败`);
  }
};

/**
 * @param {string} dbTableName
 */
export const exportTableToExcel = (dbTableName) => {
  if (!dbTableName) {
    console.error('exportTableToExcel: dbTableName is required.');
    throw new Error('导出操作缺少必要的表名参数。');
  }
  
  console.log('🔍 exportTableToExcel 被调用，参数:', { dbTableName });
  
  const exportUrl = `${apiClient.defaults.baseURL || ''}/export/${encodeURIComponent(dbTableName)}`;
  console.log('🔍 构建的导出 URL:', exportUrl);
  console.log('🔍 apiClient.defaults.baseURL:', apiClient.defaults.baseURL);
  
  try {
    console.log('🔍 尝试打开导出 URL...');
    window.open(exportUrl);
    console.log('🔍 window.open 调用完成');
  } catch (error) {
    console.error('❌ window.open 失败:', error);
    // 如果 window.open 失败，尝试直接下载
    const link = document.createElement('a');
    link.href = exportUrl;
    link.download = `${dbTableName}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    console.log('🔍 使用 link.click() 方式下载');
  }
};

export async function loadDistinctColumnValues(tabKey) {
  const endpoint = `/admintable/distinct-values/${tabKey}`;
  try {
    const response = await apiClient.get(endpoint);
    return response.data;
  } catch (error) {
    console.error(`[AdminApiService] Failed to load distinct column values for tab ${tabKey}:`, error);
    throw new Error(error.response?.data?.message || `获取 '${tabKey}' 表格的筛选选项失败`);
  }
}

/**
 * 保存报告的排序
 * @param {Array<number|string>} orderedIds - 排序后的报告ID数组
 * @returns {Promise<object>}
 */
export const saveReportOrder = async (orderedIds) => {
  try {
    const response = await apiClient.post('/reports/order', { orderedIds });
    return response.data;
  } catch (error) {
    return handleApiError(error, '保存报告顺序失败');
  }
};

export const deleteReport = async (id) => {
  try {
    const response = await apiClient.delete(`/reports/${id}`);
    return response.data;
  } catch (error) {
    return handleApiError(error, '删除报告失败');
  }
};

export async function addRemark(tableName, entityId, remark, user) {
  try {
    const response = await apiClient.post('/adminpage/remark', {
      tableName,
      entityId,
      remark,
      user
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, `为表 [${tableName}] 的实体 [${entityId}] 添加备注失败`);
  }
}

/**
 * 将临时数据上传到数据库
 * @param {string} tableName
 * @param {Array} tempRows
 * @returns {Promise<object>}
 */
export const uploadTempDataToDb = async (tableName, tempRows) => {
  try {
    const cleanRows = tempRows.map((row) => {
      // eslint-disable-next-line no-unused-vars
      const { isTemp, id, modification_log, ...cleanRow } = row;
      if (cleanRow.status === 'temp_new') {
        delete cleanRow.status;
      }
      return cleanRow;
    });

    const response = await apiClient.post('/upload/batch-insert', {
      tableName,
      data: cleanRows
    });

    const inserted = response.data?.insertedRows ?? response.data?.results?.insertedRows ?? 0;
    const errors = response.data?.results?.errors?.length ?? 0;
    if (inserted === 0 && cleanRows.length > 0) {
      throw new Error(response.data?.error || `保存失败：${errors ? `${errors} 行出错` : '未写入任何数据'}`);
    }

    return response.data;
  } catch (error) {
    return handleApiError(error, '上传临时数据到数据库失败');
  }
};

/**
 * 更新单个单元格的值
 */
export const updateCell = async (tableName, rowId, column, newValue, user = 'admin') => {
  try {
    const response = await apiClient.put(`/adminpage/cell/${tableName}/${rowId}`, { column, newValue, user });
    return response.data;
  } catch (error) {
    return handleApiError(error, `更新单元格 [${column}] 失败`);
  }
};

/**
 * 手动触发 finance bank 数据同步
 */
export const manualSyncFinanceBank = async () => {
  try {
    const response = await apiClient.post('/financeupload/manual-sync');
    return response.data;
  } catch (error) {
    throw handleApiError(error, '同步银行贷款数据失败');
  }
};